/**
 * RemnantBookPDF.js — React-PDF book layout component.
 * 
 * Single source of truth for both preview AND print.
 * Uses @react-pdf/renderer which produces identical output
 * whether rendered in-browser (PDFViewer) or to file (renderToFile).
 *
 * Install: npm install @react-pdf/renderer
 * 
 * Lulu specs (8.5"×11" hardcover casewrap, SKU 0850X1100FCSTDCW080CW444MXX):
 *   Trim size: 8.5" × 11"
 *   Page with bleed: 8.75" × 11.25" (630pt × 810pt at 72dpi)
 *   Bleed: 0.125" on all sides
 *   Safety margin: 0.5" from trim edge (minimum)
 *   Gutter: 0.75" inner margin (spine side)
 *   Minimum pages: 24
 */

import React from 'react';
import {
  Document,
  Page,
  Text,
  View,
  Image,
  Font,
  StyleSheet,
} from '@react-pdf/renderer';

// Import static font files — webpack bundles these as data URIs
import LoraRegular from './fonts/Lora-Regular.ttf';
import LoraBold from './fonts/Lora-Bold.ttf';
import LoraItalic from './fonts/Lora-Italic.ttf';
import LoraBoldItalic from './fonts/Lora-BoldItalic.ttf';

// F-207: the ONE punctuation repair, shared with the screen read views (see src/utils/storyText.js).
import { repairPunctuationSpacing } from './utils/storyText';

// ═══════════════════════════════════════════════════════════════
// FONT REGISTRATION — Lora static fonts bundled via webpack
// ═══════════════════════════════════════════════════════════════

Font.register({
  family: 'Lora',
  fonts: [
    { src: LoraRegular, fontWeight: 400 },
    { src: LoraBold, fontWeight: 700 },
    { src: LoraItalic, fontWeight: 400, fontStyle: 'italic' },
    { src: LoraBoldItalic, fontWeight: 700, fontStyle: 'italic' },
  ],
});

// Disable hyphenation for cleaner text flow
Font.registerHyphenationCallback(word => [word]);

/** Filter out AI-generated or filename-like captions — only show real user captions */
const isValidCaption = (caption) => {
  if (!caption || !caption.trim()) return false;
  const c = caption.trim();
  // Single word with no spaces (likely filename or AI label like "Luminara")
  if (!c.includes(' ') && c.length < 30) return false;
  // Looks like a filename (has extension)
  if (/\.\w{2,4}$/.test(c)) return false;
  // All caps single word
  if (c === c.toUpperCase() && !c.includes(' ')) return false;
  return true;
};

// ═══════════════════════════════════════════════════════════════
// BRAND CONSTANTS
// ═══════════════════════════════════════════════════════════════

const BRAND = {
  // Page dimensions (pts at 72dpi) — 8.5" × 11" trim + 0.125" bleed
  pageW: 8.75 * 72,      // 630pt
  pageH: 11.25 * 72,     // 810pt
  bleed: 0.125 * 72,     // 9pt

  // Margins from bleed edge — generous for hardcover binding
  marginTop: 0.875 * 72,     // 63pt — generous top margin
  marginBottom: 0.75 * 72,   // 54pt
  marginOuter: 0.75 * 72,    // 54pt
  marginInner: 1.125 * 72,   // 81pt (1.125" gutter — 0.875" effective after binding eats ~0.25")

  // Body text — elderly-readable sizes (min 13pt, generous leading)
  bodyPt: 13,
  bodyLeading: 24.05,  // 13 * 1.85 — generous spacing for elderly readers
  paraSpace: 10,

  // Title text
  chapterTitlePt: 26,
  bookTitlePt: 30,

  // Colors — all text must be high contrast for elderly readers
  textColor: '#2a2520',
  bodyColor: '#2a2520',
  mutedColor: '#5a524a',     // was #b0a090 — now dark brown, readable
  dividerColor: '#d4c4a8',
  pageBg: '#faf8f3',

  // Minimum pages
  minPages: 24,
};

// ═══════════════════════════════════════════════════════════════
// CONTENT CLEANING — strips AI prompts, markdown, test preamble
// ═══════════════════════════════════════════════════════════════

const cleanContent = (text) => {
  if (!text) return '';
  let t = text.trim();

  // ── Normalize all line break variants ──
  t = t.replace(/\r\n/g, '\n');          // Windows line breaks
  t = t.replace(/\r/g, '\n');            // Old Mac line breaks
  t = t.replace(/\u2028/g, '\n');        // Unicode line separator
  t = t.replace(/\u2029/g, '\n\n');      // Unicode paragraph separator

  // ── Fix missing spaces after sentence-ending punctuation and commas ──
  // F-207: this repair now lives in ONE place (src/utils/storyText.js) and is called by every path that
  // renders a teller's words — print AND the screen read views, which previously had no repair at all.
  // Extracted verbatim; a drift test pins that print output is byte-identical to the inline version.
  t = repairPunctuationSpacing(t);

  // ── Paragraph breaks: mirror the writing box exactly (WYSIWYG) ──
  // Every line break the author typed becomes a paragraph break, so the page
  // preview, the open-book preview, and the printed page all match what they
  // see in the editor. The renderer never invents or merges breaks — adding
  // professional paragraphing is the AI cleanup's job ("clean up" / "rewrite
  // beautifully"). Runs of blank lines collapse to one break so spacing stays even.
  t = t.replace(/\n+/g, '\n\n');

  // Remove question prompts at the start (e.g. "Question: What's the hardest...")
  t = t.replace(/^(?:Question|Prompt|Q)\s*:\s*[^\n]+\n+/i, '');
  // Also catch standalone question lines at the top (e.g. "What's the hardest thing?\n")
  t = t.replace(/^[^\n]{10,150}\?\s*\n+/, '');

  // Remove markdown headers (# Title, ## Subtitle)
  t = t.replace(/^#{1,6}\s+[^\n]+\n*/gm, '');

  // Remove "Test 1, 2, 3" or similar test preamble
  t = t.replace(/^test\s*[\d,.\s]+\.?\s*/i, '');

  // Remove markdown bold/italic markers
  t = t.replace(/\*\*([^*]+)\*\*/g, '$1');
  t = t.replace(/\*([^*]+)\*/g, '$1');
  t = t.replace(/_([^_]+)_/g, '$1');

  // Remove embedded media markers [audio:path], [video:path], and [Photo: caption]
  // [Photo:] tags are contribution captions stored in the content field; they
  // should have been stripped by cleanContributionContent but may survive if
  // the surrounding newlines don't match the expected \n\n prefix.
  t = t.replace(/\n*\[(audio|video):[^\]]+\]/g, '');
  t = t.replace(/\n*\[Photo:[^\]]*\]/g, '');

  // Remove Memory Interview metadata prefix block
  t = t.replace(/^\[Memory Interview[^\]]*\]\n+(?:Interviewed by:[^\n]*\n)?(?:Date:[^\n]*\n)?\n*/i, '');

  // Remove markdown links [text](url) → text
  t = t.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');

  // F-207: re-apply the repair AFTER markdown/link removal. Found by the TEXT-PUNCT-1 drift guard:
  // the repair above runs BEFORE the `**` strip, so in "stove,**every morning**" the comma is followed
  // by `*` (no match), and removing the markers then GLUED it — "stove,every morning" reached the
  // printed page. The repair is idempotent, so running it again is a no-op for text without markers.
  t = repairPunctuationSpacing(t);

  // Clean up multiple spaces
  t = t.replace(/ {2,}/g, ' ');

  // Clean up multiple blank lines
  t = t.replace(/\n{3,}/g, '\n\n');

  return t.trim();
};

// ═══════════════════════════════════════════════════════════════
// SMART PARAGRAPH SPLITTING
// ═══════════════════════════════════════════════════════════════

const smartParagraphs = (text) => {
  if (!text) return [];
  text = text.trim();
  if (!text) return [];
  if (text.includes('\n\n')) return text.split('\n\n').filter(p => p.trim());
  if (text.includes('\n')) return text.split('\n').filter(p => p.trim());
  // No line breaks at all — render exactly as written (a single paragraph).
  // The renderer does not fabricate paragraph structure; the AI cleanup is
  // what adds professional paragraph breaks.
  return [text];
};

// ═══════════════════════════════════════════════════════════════
// CHAPTER NUMBER WORDS
// ═══════════════════════════════════════════════════════════════

const chapterWord = (n) => {
  const words = ['One','Two','Three','Four','Five','Six','Seven','Eight','Nine','Ten','Eleven','Twelve','Thirteen','Fourteen','Fifteen','Sixteen','Seventeen','Eighteen','Nineteen','Twenty'];
  return n <= 20 ? words[n - 1] : String(n);
};

// ═══════════════════════════════════════════════════════════════
// STYLES — matched exactly to landing page interior
// ═══════════════════════════════════════════════════════════════

const s = StyleSheet.create({
  // Base pages
  pageRecto: {
    width: BRAND.pageW,
    height: BRAND.pageH,
    backgroundColor: BRAND.pageBg,
    paddingTop: BRAND.marginTop,
    paddingBottom: BRAND.marginBottom,
    paddingLeft: BRAND.marginInner,
    paddingRight: BRAND.marginOuter,
  },
  pageVerso: {
    width: BRAND.pageW,
    height: BRAND.pageH,
    backgroundColor: BRAND.pageBg,
    paddingTop: BRAND.marginTop,
    paddingBottom: BRAND.marginBottom,
    paddingLeft: BRAND.marginOuter,
    paddingRight: BRAND.marginInner,
  },
  blankPage: {
    width: BRAND.pageW,
    height: BRAND.pageH,
    backgroundColor: BRAND.pageBg,
  },

  // ── Title page (book title) ──
  titleCenter: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  brandLabel: {
    fontFamily: 'Lora',
    fontSize: 12,
    color: BRAND.mutedColor,
    letterSpacing: 4,
    marginBottom: 16,
  },
  bookTitle: {
    fontFamily: 'Lora',
    fontSize: BRAND.bookTitlePt,
    color: BRAND.textColor,
    textAlign: 'center',
    marginBottom: 8,
  },
  authorLine: {
    fontFamily: 'Lora',
    fontStyle: 'italic',
    fontSize: 13,
    color: BRAND.mutedColor,
    marginTop: 16,
  },
  subtitleLine: {
    fontFamily: 'Lora',
    fontStyle: 'italic',
    fontSize: 14,
    color: BRAND.mutedColor,
    textAlign: 'center',
    marginTop: 40,
  },
  divider: {
    width: 72,
    height: 0.5,
    backgroundColor: BRAND.dividerColor,
    alignSelf: 'center',
    marginVertical: 8,
  },

  // ── Dedication ──
  dedicationLabel: {
    fontFamily: 'Lora',
    fontSize: 12,
    color: BRAND.mutedColor,
    letterSpacing: 4,
    textAlign: 'center',
    marginBottom: 16,
  },
  dedicationText: {
    fontFamily: 'Lora',
    fontStyle: 'italic',
    fontSize: 18,
    color: BRAND.textColor,
    textAlign: 'center',
    lineHeight: 1.7,
  },

  // ── Chapter title page ──
  chapterTitleWrap: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 20,
  },
  chapterNum: {
    fontFamily: 'Lora',
    fontSize: 12,
    color: BRAND.mutedColor,
    letterSpacing: 3,
    textTransform: 'uppercase',
    textAlign: 'center',
    marginBottom: 16,
  },
  chapterTitle: {
    fontFamily: 'Lora',
    fontWeight: 700,
    fontSize: BRAND.chapterTitlePt,
    color: BRAND.textColor,
    textAlign: 'center',
    lineHeight: 1.3,
  },
  chapterAttribution: {
    fontFamily: 'Lora',
    fontStyle: 'italic',
    fontSize: 12,
    color: BRAND.mutedColor,
    textAlign: 'center',
    marginTop: 12,
  },
  chapterDivider: {
    width: 40,
    height: 0.5,
    backgroundColor: BRAND.dividerColor,
    alignSelf: 'center',
    marginTop: 20,
  },

  // ── Chapter photo ──
  chapterPhotoWrap: {
    width: '70%',
    marginBottom: 28,
    alignItems: 'center',
  },
  chapterPhoto: {
    width: '100%',
    height: 260,
    objectFit: 'cover',
  },
  caption: {
    fontFamily: 'Lora',
    fontStyle: 'italic',
    fontSize: 10,
    color: BRAND.mutedColor,
    textAlign: 'center',
    marginTop: 8,
    lineHeight: 1.5,
  },

  // ── Body text ──
  bodyPara: {
    fontFamily: 'Lora',
    fontSize: BRAND.bodyPt,
    color: BRAND.bodyColor,
    lineHeight: BRAND.bodyLeading / BRAND.bodyPt,
    textIndent: 18,
    textAlign: 'left',
    marginBottom: BRAND.paraSpace,
  },

  // ── Bold first words (chapter opener) ──
  openerBold: {
    fontFamily: 'Lora',
    fontWeight: 700,
    fontSize: BRAND.bodyPt,
    color: BRAND.textColor,
  },
  openerRest: {
    fontFamily: 'Lora',
    fontSize: BRAND.bodyPt,
    color: BRAND.bodyColor,
  },
  openerPara: {
    fontFamily: 'Lora',
    fontSize: BRAND.bodyPt,
    color: BRAND.bodyColor,
    lineHeight: BRAND.bodyLeading / BRAND.bodyPt,
    textAlign: 'left',
    marginBottom: BRAND.paraSpace,
  },

  // ── Text page header ──
  textPageHeader: {
    fontFamily: 'Lora',
    fontStyle: 'italic',
    fontSize: 11,
    color: BRAND.mutedColor,
    marginBottom: 20,
    letterSpacing: 0.5,
  },
  textPageDivider: {
    width: 40,
    height: 0.5,
    backgroundColor: BRAND.dividerColor,
    marginBottom: 24,
  },

  // ── Inline photo ──
  inlinePhotoWrap: {
    width: '100%',
    marginBottom: 16,
    alignItems: 'center',
  },
  inlinePhoto: {
    width: '85%',
    height: 240,
    objectFit: 'cover',
  },
  inlineCaption: {
    fontFamily: 'Lora',
    fontStyle: 'italic',
    fontSize: 10,
    color: BRAND.mutedColor,
    textAlign: 'center',
    marginTop: 8,
    marginBottom: 16,
    lineHeight: 1.5,
  },

  // ── Photo spread overlay ──
  photoOverlayTitle: {
    fontFamily: 'Lora',
    fontStyle: 'italic',
    fontSize: 22,
    color: '#faf8f3',
  },
  photoOverlayCaption: {
    fontFamily: 'Lora',
    fontStyle: 'italic',
    fontSize: 10,
    color: 'rgba(250,248,243,0.7)',
  },

  // ── Story page ──
  storyPageDivider: {
    width: 40,
    height: 0.5,
    backgroundColor: BRAND.dividerColor,
    marginVertical: 12,
  },

  // ── Running footer ──
  footerText: {
    fontFamily: 'Lora',
    fontSize: 11,
    color: BRAND.textColor,
    position: 'absolute',
    bottom: 24,
    letterSpacing: 0.5,
  },

  // ── Rapid Fire Q&A layout (TB-104 Hot Seat Q&A) ──
  // Used only when chapter content starts with the [RAPID-FIRE-QA] sentinel.
  // Prose chapters are never affected.
  qaQuestion: {
    fontFamily: 'Lora',
    fontStyle: 'italic',
    fontSize: 11,
    color: BRAND.mutedColor,
    lineHeight: 1.45,
    marginBottom: 3,
  },
  qaAnswer: {
    fontFamily: 'Lora',
    fontSize: BRAND.bodyPt,
    color: BRAND.bodyColor,
    lineHeight: BRAND.bodyLeading / BRAND.bodyPt,
    marginBottom: 18,
  },
  qaHeaderLabel: {
    fontFamily: 'Lora',
    fontSize: 10,
    color: BRAND.mutedColor,
    letterSpacing: 2.5,
    textTransform: 'uppercase',
    marginBottom: 4,
  },

  // ── End page ──
  endText: {
    fontFamily: 'Lora',
    fontStyle: 'italic',
    fontSize: 13,
    color: BRAND.mutedColor,
    textAlign: 'center',
    lineHeight: 1.6,
  },
});


// ═══════════════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════════════

const getPageStyle = (pageNum) =>
  pageNum % 2 === 1 ? s.pageRecto : s.pageVerso;

// ═══════════════════════════════════════════════════════════════
// PAGE COMPONENTS
// ═══════════════════════════════════════════════════════════════

/**
 * Running footer — matches landing page exactly:
 *   Recto (odd):  familyName .............. pageNum
 *   Verso (even): pageNum .............. bookTitle
 */
// Page numbers on the outer edge of each spread (recto = right, verso =
// left). Standard publishing convention. No familyName or bookTitle —
// repeating those on every page reads as noise on a keepsake book.
// hideFolio: REVEAL-001 C1 — in the reveal single-chapter render the Document
// starts at page 1, so react-pdf's page-relative folio would print a misleading
// "1" (etc.) under "prints exactly like this". Suppress folios entirely in that
// mode. Normal book render passes nothing → folios render as before.
const RunningFooter = ({ hideFolio } = {}) => {
  if (hideFolio) return null;
  return (
    <>
      <Text
        style={{ ...s.footerText, left: BRAND.marginOuter }}
        render={({ pageNumber }) => (pageNumber <= 2 || pageNumber % 2 === 1 ? '' : pageNumber)}
        fixed
      />
      <Text
        style={{ ...s.footerText, right: BRAND.marginOuter }}
        render={({ pageNumber }) => (pageNumber <= 2 || pageNumber % 2 === 0 ? '' : pageNumber)}
        fixed
      />
    </>
  );
};

/** QR code badge — centered, in-flow, placed at the START of a chapter (under
 *  the chapter title), mirroring Remento's "scan at the start of the chapter"
 *  model. Centering keeps it clear of the gutter/crease (easy to scan); being
 *  in-flow at the top guarantees it never overlaps the body text and never gets
 *  pushed onto a page of its own (the failure mode of an end-of-text badge).
 *  `wrap={false}` keeps the QR + label together across page breaks. */
const QRBadge = ({ qrDataUrl }) => {
  if (!qrDataUrl) return null;
  // Neutral verb — the recording behind a QR may be voice OR video, so avoid
  // "watch"/"hear" which would be wrong for one of them.
  const label = 'Scan to play this story';
  return (
    <View wrap={false} style={{ marginTop: 2, marginBottom: 16, alignItems: 'center' }}>
      <Image src={qrDataUrl} style={{ width: 58, height: 58, marginBottom: 4 }} />
      <Text style={{ fontFamily: 'Lora', fontStyle: 'italic', fontSize: 7.5, color: BRAND.mutedColor, textAlign: 'center', lineHeight: 1.4 }}>{label}</Text>
    </View>
  );
};

/** Blank page */
const BlankPage = () => <Page size={[BRAND.pageW, BRAND.pageH]} style={s.blankPage} />;

/** Book title page — minimal, no template subtitles or "by" prefix */
const TitlePage = ({ title, author }) => (
  <Page size={[BRAND.pageW, BRAND.pageH]} style={getPageStyle(2)}>
    <View style={s.titleCenter}>
      <Text style={{ ...s.brandLabel, letterSpacing: 2 }}>Talebound</Text>
      <View style={s.divider} />
      <Text style={s.bookTitle}>{title}</Text>
      <View style={s.divider} />
      {author ? <Text style={s.authorLine}>{author}</Text> : null}
    </View>
  </Page>
);

/** Dedication page */
const DedicationPage = ({ text }) => (
  <Page size={[BRAND.pageW, BRAND.pageH]} style={getPageStyle(3)}>
    <View style={s.titleCenter}>
      <Text style={s.dedicationLabel}>DEDICATION</Text>
      <View style={s.divider} />
      <Text style={s.dedicationText}>{text}</Text>
    </View>
  </Page>
);

/** Bold first words — first ~6 words of the chapter opener in bold */
const BoldOpenerParagraph = ({ text }) => {
  if (!text || text.length < 2) return <Text style={s.openerPara}>{text || ''}</Text>;
  // Find a natural break point around 6 words
  const words = text.split(' ');
  const boldCount = Math.min(Math.max(5, words.findIndex((w, i) => i >= 4 && /[,;.!?—]/.test(w)) + 1), 8) || 6;
  const boldPart = words.slice(0, boldCount).join(' ');
  const restPart = words.slice(boldCount).join(' ');
  return (
    <Text style={s.openerPara}>
      <Text style={s.openerBold}>{boldPart}</Text>
      <Text style={s.openerRest}>{restPart ? ' ' + restPart : ''}</Text>
    </Text>
  );
};

/** 
 * Classic chapter title page — centered, elegant, with optional photo.
 * Matches the landing page left-page layout.
 */
const ClassicTitlePage = ({ title, chapterNum, attribution, photoUrl, caption, pageNum, familyName, bookTitle, photoFocusX, photoFocusY, photoOrientation, qrDataUrl, mediaType, hideFolio }) => (
  <Page size={[BRAND.pageW, BRAND.pageH]} style={getPageStyle(pageNum)}>
    <View style={s.chapterTitleWrap}>
      {photoUrl ? (
        <View style={photoOrientation === 'portrait' ? { width: '50%', marginBottom: 28, alignItems: 'center' } : s.chapterPhotoWrap}>
          <Image src={photoUrl} style={photoOrientation === 'portrait'
            ? { width: '100%', height: 320, objectFit: 'cover', objectPosition: `${photoFocusX ?? 50}% ${photoFocusY ?? 50}%` }
            : { ...s.chapterPhoto, objectPosition: `${photoFocusX ?? 50}% ${photoFocusY ?? 50}%` }} />
          {isValidCaption(caption) ? <Text style={s.caption}>{caption}</Text> : null}
        </View>
      ) : null}
      {chapterNum ? <Text style={s.chapterNum}>{`Chapter ${chapterWord(chapterNum)}`}</Text> : null}
      <Text style={s.chapterTitle}>{title}</Text>
      {attribution ? <Text style={s.chapterAttribution}>{attribution}</Text> : null}
      <View style={s.chapterDivider} />
    </View>
    {/* QR sits in the open lower area of the title page — centered (clear of the
        gutter), never colliding with the centered title block above it. */}
    {qrDataUrl ? (
      <View style={{ position: 'absolute', bottom: BRAND.marginBottom + 12, left: 0, right: 0, alignItems: 'center' }}>
        <Image src={qrDataUrl} style={{ width: 64, height: 64, marginBottom: 5 }} />
        <Text style={{ fontFamily: 'Lora', fontStyle: 'italic', fontSize: 8, color: BRAND.mutedColor, textAlign: 'center' }}>Scan to play this story</Text>
      </View>
    ) : null}
    <RunningFooter familyName={familyName} bookTitle={bookTitle} hideFolio={hideFolio} />
  </Page>
);

/**
 * Text page — body text with drop cap on first paragraph.
 * For classic layout: shows chapter title as small italic header, divider, then drop cap + body.
 * For photo-spread facing page: can show inline photo + caption at top, then drop cap + body.
 */
const TextPage = ({ content, pageNum, familyName, bookTitle, chapterTitle, photoUrl, caption, isFirstTextPage, qrDataUrl, mediaType, hideFolio }) => {
  const cleaned = cleanContent(content);
  const paras = smartParagraphs(cleaned);
  if (!paras.length) return null;

  return (
    <Page size={[BRAND.pageW, BRAND.pageH]} style={getPageStyle(pageNum)} wrap>
      {/* First text page gets a header or inline photo */}
      {isFirstTextPage && photoUrl ? (
        <View style={s.inlinePhotoWrap}>
          <Image src={photoUrl} style={s.inlinePhoto} />
          {isValidCaption(caption) ? <Text style={s.inlineCaption}>{caption}</Text> : null}
        </View>
      ) : isFirstTextPage && chapterTitle ? (
        <>
          <Text style={s.textPageHeader}>{chapterTitle}</Text>
          <View style={s.textPageDivider} />
        </>
      ) : null}

      {/* QR at the start of the chapter (under the header), so it can't be
          pushed onto a page of its own the way an end-of-text badge can. */}
      {isFirstTextPage && <QRBadge qrDataUrl={qrDataUrl} mediaType={mediaType} />}

      {/* Body text — drop cap on first paragraph of first text page */}
      {paras.map((para, i) => (
        i === 0 && isFirstTextPage
          ? <BoldOpenerParagraph key={i} text={para} />
          : <Text key={i} style={s.bodyPara}>{para}</Text>
      ))}
      <RunningFooter familyName={familyName} bookTitle={bookTitle} hideFolio={hideFolio} />
    </Page>
  );
};

/** Photo Spread: museum-mat framed photo with a slim dark overlay band at
 *  the bottom (just enough to back the title text legibly). Photo size
 *  unchanged. Band is much smaller and lighter than the original. */
const PhotoSpreadPage = ({ title, chapterNum, attribution, photoUrl, caption, pageNum, familyName, bookTitle, photoFocusX, photoFocusY, hideFolio }) => {
  const matInset = 48; // 0.67" white mat border
  const bandHeight = (BRAND.pageH - matInset * 2) * 0.15; // 15% of photo (was 35%)
  return (
  <Page size={[BRAND.pageW, BRAND.pageH]} style={{ width: BRAND.pageW, height: BRAND.pageH, position: 'relative', backgroundColor: '#faf8f3' }}>
    {photoUrl ? (
      <Image src={photoUrl} style={{
        position: 'absolute',
        top: matInset, left: matInset,
        width: BRAND.pageW - matInset * 2,
        height: BRAND.pageH - matInset * 2,
        objectFit: 'cover',
        objectPosition: `${photoFocusX ?? 50}% ${photoFocusY ?? 50}%`,
      }} />
    ) : null}
    {/* Slim dark band — only behind the title text */}
    <View style={{
      position: 'absolute',
      bottom: matInset,
      left: matInset, right: matInset,
      height: bandHeight,
      backgroundColor: 'rgba(0,0,0,0.40)', // was 0.55
    }} />
    <View style={{
      position: 'absolute',
      bottom: matInset + 16,
      left: BRAND.marginInner, right: BRAND.marginOuter,
    }}>
      {chapterNum ? <Text style={{
        fontFamily: 'Lora', fontSize: 8.5, color: 'rgba(250,248,243,0.55)', letterSpacing: 2.5, marginBottom: 3,
      }}>{`CHAPTER ${chapterWord(chapterNum).toUpperCase()}`}</Text> : null}
      <Text style={{
        fontFamily: 'Lora', fontStyle: 'italic', fontSize: 16, color: '#faf8f3', lineHeight: 1.2,
      }}>{title}</Text>
      {attribution ? <Text style={{
        fontFamily: 'Lora', fontStyle: 'italic', fontSize: 9, color: 'rgba(250,248,243,0.7)', marginTop: 3,
      }}>{attribution}</Text> : null}
    </View>
    <RunningFooter familyName={familyName} bookTitle={bookTitle} hideFolio={hideFolio} />
  </Page>
  );
};

/** Photo-Full: pure full-bleed photo, mat border only, no title band or text.
 *  Use when a chapter is intentionally photo-only (placeholder text, photo
 *  is the entire content). Caption optional (shown small in mat below photo
 *  if present). Running footer still renders for page count consistency. */
const PhotoFullPage = ({ photoUrl, caption, pageNum, familyName, bookTitle, photoFocusX, photoFocusY, hideFolio }) => {
  const matInset = 48; // 0.67" white mat border (matches PhotoSpreadPage)
  return (
  <Page size={[BRAND.pageW, BRAND.pageH]} style={{ width: BRAND.pageW, height: BRAND.pageH, position: 'relative', backgroundColor: '#faf8f3' }}>
    {photoUrl ? (
      <Image src={photoUrl} style={{
        position: 'absolute',
        top: matInset, left: matInset,
        width: BRAND.pageW - matInset * 2,
        height: BRAND.pageH - matInset * 2,
        objectFit: 'cover',
        objectPosition: `${photoFocusX ?? 50}% ${photoFocusY ?? 50}%`,
      }} />
    ) : null}
    {isValidCaption(caption) ? (
      <Text style={{
        position: 'absolute',
        bottom: matInset / 2 - 6,
        left: matInset, right: matInset,
        fontFamily: 'Lora', fontStyle: 'italic', fontSize: 9,
        color: 'rgba(60,70,83,0.6)', textAlign: 'center',
      }}>{caption}</Text>
    ) : null}
    <RunningFooter familyName={familyName} bookTitle={bookTitle} hideFolio={hideFolio} />
  </Page>
  );
};

/** Story Page: photo + title + text on one page, wraps to additional pages */
const StoryPageChapter = ({ title, chapterNum, attribution, photoUrl, caption, content, startPageNum, familyName, bookTitle, photoFocusX, photoFocusY, qrDataUrl, mediaType, hideFolio }) => {
  const cleaned = cleanContent(content);
  const paras = smartParagraphs(cleaned);

  return (
    <Page size={[BRAND.pageW, BRAND.pageH]} style={getPageStyle(startPageNum)} wrap>
      {/* Photo area */}
      {photoUrl ? (
        <View style={{ marginBottom: 12 }}>
          <Image src={photoUrl} style={{ width: '100%', height: 360, objectFit: 'cover', objectPosition: `${photoFocusX ?? 50}% ${photoFocusY ?? 50}%` }} />
          {isValidCaption(caption) ? <Text style={{ ...s.caption, marginTop: 8 }}>{caption}</Text> : null}
        </View>
      ) : null}

      {/* Chapter label */}
      {chapterNum ? <Text style={{ ...s.chapterNum, textAlign: 'left', marginBottom: 8 }}>{`Chapter ${chapterWord(chapterNum)}`}</Text> : null}

      {/* Title + Attribution + Divider */}
      <Text style={{ ...s.chapterTitle, textAlign: 'left', fontSize: 20, marginBottom: 4 }}>{title}</Text>
      {attribution ? <Text style={{ ...s.chapterAttribution, textAlign: 'left', marginTop: 4, marginBottom: 8 }}>{attribution}</Text> : null}
      <View style={s.storyPageDivider} />

      {/* QR at the start of the chapter (under the title) — never overflows onto
          its own page the way an end-of-text badge can. */}
      <QRBadge qrDataUrl={qrDataUrl} mediaType={mediaType} />

      {/* Body text with drop cap on first paragraph */}
      {paras.map((para, i) => (
        i === 0
          ? <BoldOpenerParagraph key={i} text={para} />
          : <Text key={i} style={s.bodyPara}>{para}</Text>
      ))}
      <RunningFooter familyName={familyName} bookTitle={bookTitle} hideFolio={hideFolio} />
    </Page>
  );
};

/** End page — colophon */
const EndPage = ({ pageNum, occasion, recipientName, organizerName, contributorCount }) => (
  <Page size={[BRAND.pageW, BRAND.pageH]} style={getPageStyle(pageNum)}>
    <View style={s.titleCenter}>
      <View style={s.divider} />
      <Text style={s.endText}>
        {recipientName
          ? `For ${recipientName}, with love.`
          : `Made with love.`}
      </Text>
      <View style={{ marginTop: 20 }} />
      <Text style={{ ...s.brandLabel, marginBottom: 0, letterSpacing: 2 }}>Talebound</Text>
      <View style={s.divider} />
    </View>
  </Page>
);

/** Padding page — soft prompt, no lines, no frames. Used as Lulu-minimum
 *  padding so unused pages feel like deliberate breathing room. */
const NotesPage = ({ pageNum, label = 'For your own thoughts' }) => (
  <Page size={[BRAND.pageW, BRAND.pageH]} style={getPageStyle(pageNum)}>
    <View style={{ flex: 1, paddingTop: 80, alignItems: 'center' }}>
      <Text style={{
        fontFamily: 'Lora',
        fontStyle: 'italic',
        fontSize: 12,
        color: BRAND.mutedColor,
      }}>{label}</Text>
    </View>
  </Page>
);

// ═══════════════════════════════════════════════════════════════
// MAIN DOCUMENT COMPONENT
// ═══════════════════════════════════════════════════════════════

/**
 * RemnantBookPDF — The complete book as a react-pdf Document.
 * 
 * Props:
 *   title: string — book title
 *   author: string — author name
 *   familyName: string — family name for running footer
 *   dedication: string — dedication text
 *   isCaretaker: boolean — true if caretaker path (shows "As told by")
 *   lovedOneName: string — name of loved one (caretaker path)
 *   chapters: array of { title, content, photoUrl, caption, layout, toldBy, subtitle, qrDataUrl, mediaType }
 *   qrDataUrl/mediaType on each chapter: optional QR code data URL and media type for printed QR badge
 */
// ═══════════════════════════════════════════════════════════════
// RAPID FIRE Q&A HELPERS (TB-104 Hot Seat Q&A)
// ═══════════════════════════════════════════════════════════════

/**
 * Detects the [RAPID-FIRE-QA] sentinel written by generateHotSeatStory.
 * Returns true only for Hot Seat Q&A chapters; all prose chapters return false.
 * Backward compatible: any chapter without this exact prefix is unaffected.
 */
const isRapidFireContent = (content) => // TB-104 Hot Seat Q&A
  typeof content === 'string' && content.trimStart().startsWith('[RAPID-FIRE-QA]');

// ═══════════════════════════════════════════════════════════════
// FILLER CHAPTER SENTINEL (demo / print-thickness tool)
// ═══════════════════════════════════════════════════════════════

/**
 * Detects a filler chapter injected for demo print thickness.
 * Sentinel format: [FILLER-PAGES:N] where N is the number of blank pages to render.
 * These chapters render as pure blank pages (no title, no footer) so the spine
 * width calculation sees the correct total page count. They are invisible to readers.
 * Non-destructive: removing the page from the book restores the original book.
 */
const isFillerContent = (content) =>
  typeof content === 'string' && /^\s*\[FILLER-PAGES:\d+\]/.test(content);

/** Parse the page count from a filler sentinel. Returns 0 if malformed. */
const parseFillerPageCount = (content) => {
  const m = content && content.match(/\[FILLER-PAGES:(\d+)\]/);
  return m ? Math.max(0, parseInt(m[1], 10)) : 0;
};

/**
 * Parses a sentinel Q&A block into [{question, answer}] pairs.
 * Format written by generateHotSeatStory:
 *   [RAPID-FIRE-QA]
 *   Q: <question text>
 *   A: <answer text>
 *
 *   Q: ...
 *   A: ...
 */
const parseRapidFireContent = (content) => { // TB-104 Hot Seat Q&A
  const body = content.replace('[RAPID-FIRE-QA]', '').trim();
  const blocks = body.split(/\n{2,}/);
  const pairs = [];
  for (const block of blocks) {
    const lines = block.trim().split('\n');
    const qLine = lines.find(l => l.startsWith('Q: '));
    const aLine = lines.find(l => l.startsWith('A: '));
    if (qLine && aLine) {
      pairs.push({ question: qLine.slice(3).trim(), answer: aLine.slice(3).trim() });
    }
  }
  return pairs;
};

/**
 * RapidFirePage — renders a Hot Seat Q&A chapter as a styled magazine-style
 * rapid-fire round. Question: Lora italic, muted color. Answer: Lora body weight.
 * Multiple Q&As flow down the page; @react-pdf `wrap` handles pagination cleanly.
 * QR badge renders at the top if present (same placement as prose chapters).
 * TB-104 Hot Seat Q&A — only reached when isRapidFireContent() is true.
 */
const RapidFirePage = ({ title, chapterNum, qaPairs, startPageNum, familyName, bookTitle, qrDataUrl, mediaType, hideFolio }) => ( // TB-104 Hot Seat Q&A
  <Page size={[BRAND.pageW, BRAND.pageH]} style={getPageStyle(startPageNum)} wrap>
    {/* Small italic chapter header + rule — mirrors TextPage header style */}
    <Text style={s.textPageHeader}>{title}</Text>
    <View style={s.textPageDivider} />
    {/* QR badge at top so it's never pushed onto its own page */}
    <QRBadge qrDataUrl={qrDataUrl} mediaType={mediaType} />
    {/* Q&A pairs — each pair is a non-splitting unit */}
    {qaPairs.map((pair, i) => (
      <View key={i} wrap={false} style={{ marginBottom: 4 }}>
        <Text style={s.qaQuestion}>{pair.question}</Text>
        <Text style={s.qaAnswer}>{pair.answer}</Text>
      </View>
    ))}
    <RunningFooter familyName={familyName} bookTitle={bookTitle} hideFolio={hideFolio} />
  </Page>
);

/** If a chapter's body opens with a line that just repeats the chapter title
 *  (a common copy-paste artifact), drop it — the title already prints as the
 *  page header / title page, so otherwise it shows twice. */
const stripLeadingTitleLine = (content, title) => {
  if (!content || !title) return content || '';
  const norm = (str) => str.trim().toLowerCase().replace(/[.;:!?]+$/, '');
  const target = norm(title);
  if (!target) return content;
  const lines = content.replace(/\r/g, '').split('\n');
  let i = 0;
  while (i < lines.length && !lines[i].trim()) i++; // skip leading blank lines
  if (i < lines.length && norm(lines[i]) === target) {
    lines.splice(0, i + 1);
    while (lines.length && !lines[0].trim()) lines.shift(); // drop blanks left behind
    return lines.join('\n');
  }
  return content;
};

/**
 * buildChapterNodes — emits the react-pdf Page(s) for ONE chapter.
 *
 * Extracted (REVEAL-001 C1) so the full-book render and the single-chapter
 * "reveal" render share ONE copy of the per-chapter layout branch logic — no
 * in-file duplication (Change Protocol rule 3). Both callers get byte-identical
 * pagination by construction, which is what the reveal's print-parity claim
 * ("prints exactly like this") rests on.
 *
 *   ch           the chapter object
 *   i            zero-based chapter index (drives keys + "Chapter N")
 *   firstPageNum the page number of this chapter's FIRST page (recto/verso side)
 *   ctx          { isCaretaker, lovedOneName, familyName, bookTitle, hideFolio }
 *
 * Returns { nodes, endPageNum } — endPageNum is the page number of the LAST
 * page this chapter emitted, so the caller can continue numbering.
 */
const buildChapterNodes = (ch, i, firstPageNum, ctx) => {
  const { isCaretaker, lovedOneName, familyName, bookTitle, hideFolio, chapterNumber } = ctx;
  const nodes = [];
  let pageNum = firstPageNum;
  const layout = ch.layout || 'classic';
  const hasPhoto = !!ch.photoUrl;
  // In the full book the chapter's number is its position (i + 1). In the reveal
  // render a single chapter is passed but must still show its REAL position
  // ("Chapter Three"), so the caller can override via ctx.chapterNumber.
  const chNum = chapterNumber != null ? chapterNumber : i + 1;
  // Drop a leading body line that merely repeats the chapter title (paste artifact)
  const chBody = stripLeadingTitleLine(ch.content, ch.title);

  // Attribution logic
  let attribution = null;
  if (ch.isKidStory && ch.contributorName) {
    attribution = `In ${ch.contributorName}'s words`;
  } else if (ch.contributorName) {
    attribution = `By ${ch.contributorName}${ch.contributorRelationship ? `, ${ch.contributorRelationship}` : ''}`;
  } else if (isCaretaker) {
    const teller = ch.toldBy || lovedOneName || '';
    if (teller) attribution = `As told by ${teller}`;
  } else {
    if (ch.subtitle) attribution = ch.subtitle;
  }

  // TB-104 Hot Seat Q&A: detect the sentinel before any layout check.
  if (isRapidFireContent(ch.content)) {
    const qaPairs = parseRapidFireContent(ch.content);
    if (qaPairs.length > 0) {
      nodes.push(
        <RapidFirePage
          key={`ch${i}-qa`}
          title={ch.title || 'Rapid Fire'}
          chapterNum={chNum}
          qaPairs={qaPairs}
          startPageNum={pageNum}
          familyName={familyName}
          bookTitle={bookTitle}
          qrDataUrl={ch.qrDataUrl} mediaType={ch.mediaType}
          hideFolio={hideFolio}
        />
      );
      return { nodes, endPageNum: pageNum };
    }
  }

  // Filler chapter: renders N pure blank pages for demo print thickness.
  if (isFillerContent(ch.content)) {
    const count = parseFillerPageCount(ch.content);
    if (count > 0) {
      nodes.push(<BlankPage key={`filler-${i}-0`} />);
      for (let f = 1; f < count; f++) {
        pageNum++;
        nodes.push(<BlankPage key={`filler-${i}-${f}`} />);
      }
      return { nodes, endPageNum: pageNum };
    }
  }

  if (layout === 'story-page' && hasPhoto) {
    nodes.push(
      <StoryPageChapter
        key={`ch${i}`}
        title={ch.title || `Chapter ${chapterWord(chNum)}`}
        chapterNum={chNum}
        attribution={attribution}
        photoUrl={ch.photoUrl}
        caption={ch.caption || ''}
        content={chBody || ''}
        startPageNum={pageNum}
        familyName={familyName}
        bookTitle={bookTitle}
        photoFocusX={ch.photoFocusX} photoFocusY={ch.photoFocusY}
        qrDataUrl={ch.qrDataUrl} mediaType={ch.mediaType}
        hideFolio={hideFolio}
      />
    );

  } else if (layout === 'photo-full' && hasPhoto) {
    nodes.push(
      <PhotoFullPage
        key={`ch${i}-photofull`}
        photoUrl={ch.photoUrl}
        caption={ch.caption || ''}
        pageNum={pageNum}
        familyName={familyName}
        bookTitle={bookTitle}
        photoFocusX={ch.photoFocusX} photoFocusY={ch.photoFocusY}
        hideFolio={hideFolio}
      />
    );
    // photo-full intentionally has no following text page

  } else if (layout === 'photo-spread' && hasPhoto) {
    nodes.push(
      <PhotoSpreadPage
        key={`ch${i}-title`}
        title={ch.title || `Chapter ${chapterWord(chNum)}`}
        chapterNum={chNum}
        attribution={attribution}
        photoUrl={ch.photoUrl}
        caption={ch.caption || ''}
        pageNum={pageNum}
        familyName={familyName}
        bookTitle={bookTitle}
        photoFocusX={ch.photoFocusX} photoFocusY={ch.photoFocusY}
        hideFolio={hideFolio}
      />
    );
    if (chBody && chBody.trim()) {
      pageNum++;
      nodes.push(
        <TextPage
          key={`ch${i}-text`}
          content={chBody}
          pageNum={pageNum}
          familyName={familyName}
          bookTitle={bookTitle}
          chapterTitle={ch.title}
          isFirstTextPage={true}
          qrDataUrl={ch.qrDataUrl} mediaType={ch.mediaType}
          hideFolio={hideFolio}
        />
      );
    }

  } else {
    // Classic layout
    nodes.push(
      <ClassicTitlePage
        key={`ch${i}-title`}
        title={ch.title || `Chapter ${chapterWord(chNum)}`}
        chapterNum={chNum}
        attribution={attribution}
        photoUrl={hasPhoto ? ch.photoUrl : null}
        caption={ch.caption || ''}
        pageNum={pageNum}
        familyName={familyName}
        bookTitle={bookTitle}
        photoFocusX={ch.photoFocusX} photoFocusY={ch.photoFocusY}
        photoOrientation={ch.photoOrientation}
        qrDataUrl={ch.qrDataUrl} mediaType={ch.mediaType}
        hideFolio={hideFolio}
      />
    );
    if (chBody && chBody.trim()) {
      pageNum++;
      nodes.push(
        <TextPage
          key={`ch${i}-text`}
          content={chBody}
          pageNum={pageNum}
          familyName={familyName}
          bookTitle={bookTitle}
          chapterTitle={ch.title}
          isFirstTextPage={true}
          hideFolio={hideFolio}
        />
      );
    }
  }

  return { nodes, endPageNum: pageNum };
};

const RemnantBookPDF = ({
  title = 'My Stories',
  author = '',
  familyName = '',
  dedication = '',
  isCaretaker = false,
  lovedOneName = '',
  chapters = [],
  occasion = null,
  recipientName = '',
  contributorCount = 0,
  // Phase 2 TB-102: when true, appends one extra BlankPage after the colophon
  // to guarantee an even rendered page count. Only set this when a prior render
  // read an odd numPages from pdf.js — never set it on screen previews.
  forceEvenPages = false,
  // REVEAL-001 C1: single-chapter "reveal" render. When true, emit ONLY the
  // chapter's page(s) — no title page, dedication, Notes padding, colophon, or
  // even-page padding — with folios suppressed, so the reveal shows exactly the
  // pages this one chapter prints. Uses the SAME buildChapterNodes as the full
  // book, so pagination is print-identical. Screen-only; never used for print.
  revealMode = false,
  // REVEAL-001 C1: the chapter's REAL 1-based position in the book, so the
  // standalone reveal render shows "Chapter Three" rather than "Chapter One".
  // Only meaningful with revealMode + a single-chapter `chapters` array.
  revealChapterNumber = null,
}) => {
  // Reveal path: only the chapter(s), no book framing, folios hidden.
  if (revealMode) {
    // REVEAL-001 Option B: the reveal renders the chapter BODY only. The chapter's title block
    // (kicker + title + attribution + divider) is COMPOSED as DOM by RevealPages and shown atop
    // page 1 for reading — so the reveal omits the standalone print title page AND the per-page
    // title header. Body words + page splits still come from THIS real react-pdf render (wrap
    // paginates the body); only the title PRESENTATION is composed. Print (non-reveal) is unchanged.
    const ch = chapters[0] || {};
    const revealPages = [];
    if (isRapidFireContent(ch.content) || isFillerContent(ch.content)) {
      // Rapid-fire / filler already combine onto one page — render as-is (folios hidden).
      const ctx = { isCaretaker, lovedOneName, familyName, bookTitle: title, hideFolio: true, chapterNumber: revealChapterNumber };
      const { nodes } = buildChapterNodes(ch, 0, 1, ctx);
      revealPages.push(...nodes);
    } else {
      const chBody = stripLeadingTitleLine(ch.content, ch.title);
      // isFirstTextPage keeps the bold opener + QR badge; NO chapterTitle → no per-page header.
      revealPages.push(
        <TextPage
          key="reveal-body"
          content={chBody}
          pageNum={1}
          familyName={familyName}
          bookTitle={title}
          photoUrl={ch.photoUrl || null}
          caption={ch.caption || ''}
          isFirstTextPage
          qrDataUrl={ch.qrDataUrl}
          mediaType={ch.mediaType}
          hideFolio
        />
      );
    }
    return (
      <Document
        title={title}
        author={author || 'Talebound'}
        subject="A keepsake book of stories, memories, and love."
        creator="Talebound — taleboundbook.com"
      >
        {revealPages}
      </Document>
    );
  }

  let pageNum = 0;
  const pages = [];

  // Page 1 (recto): Title page
  pageNum++;
  pages.push(<TitlePage key={`p${pageNum}`} title={title} author={author} occasion={occasion} recipientName={recipientName} />);

  // Page 2 (verso): Dedication
  pageNum++;
  if (dedication && dedication.trim()) {
    pages.push(<DedicationPage key={`p${pageNum}`} text={dedication} />);
  } else {
    pages.push(<BlankPage key={`p${pageNum}`} />);
  }

  // Chapters — one shared emission path (buildChapterNodes) for full book + reveal.
  const chapterCtx = { isCaretaker, lovedOneName, familyName, bookTitle: title, hideFolio: false };
  for (let i = 0; i < chapters.length; i++) {
    pageNum++; // first page of this chapter
    const { nodes, endPageNum } = buildChapterNodes(chapters[i], i, pageNum, chapterCtx);
    pages.push(...nodes);
    pageNum = endPageNum;
  }

  // Pad to Lulu minimum 24 pages with Notes pages BEFORE the colophon, so the
  // colophon ("For X, with love. / Talebound") is always the LAST page.
  // First padding page is labeled "From All of Us" (family signature/drawing
  // space). Subsequent padding pages are "For your own thoughts" (recipient's
  // free space). Both are unlined, no frames — trusts the user.
  let isFirstPad = true;
  while (pageNum + 1 < BRAND.minPages) {
    pageNum++;
    pages.push(<NotesPage key={`notes-${pageNum}`} pageNum={pageNum} label={isFirstPad ? 'From All of Us' : undefined} />);
    isFirstPad = false;
  }

  // End page (colophon) — always the last content page
  pageNum++;
  pages.push(<EndPage key={`end`} pageNum={pageNum} occasion={occasion} recipientName={recipientName} organizerName={author} contributorCount={contributorCount} />);

  // Pad to even page count if still odd (component-level even-pad)
  if (pageNum % 2 !== 0) {
    pageNum++;
    pages.push(<BlankPage key={`pad-even`} />);
  }

  // TB-102 Phase 2: if the caller detected an odd *rendered* page count (via
  // pdf.js numPages) and set forceEvenPages, append one more BlankPage so the
  // re-render comes out even. This runs AFTER the component-level even-pad so
  // the colophon (EndPage) always stays last before this trailing blank.
  // Only used in the checkout/print path — never in screen previews.
  if (forceEvenPages) {
    pages.push(<BlankPage key={`pad-even-rendered`} />);
  }

  return (
    <Document
      title={title}
      author={author || 'Talebound'}
      subject="A keepsake book of stories, memories, and love."
      creator="Talebound — taleboundbook.com"
    >
      {pages}
    </Document>
  );
};

export default RemnantBookPDF;
export { BRAND, smartParagraphs, cleanContent, isRapidFireContent, parseRapidFireContent, isFillerContent, parseFillerPageCount, chapterWord }; // TB-104 Hot Seat Q&A; filler sentinel; chapterWord (REVEAL-001 composed kicker)